/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { basicDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { useRemScanData } from "./hooks";
import ConfirmDialog from "../../Shared/Dialogs/ConfirmDialog";
import { DIALOGOPENFOR } from "../../../globals/globals";
import AutocompleteSelect from "../../Shared/Dropdown/AutocompleteSelect";
import { IOption } from "../../Shared/Filter/types";
import { Loader2 } from "lucide-react";


interface IRemScanProps {
    modelOpenMode?: string;
    zoneOption?: IOption[];
}

const RemScanModal: React.FC<IRemScanProps> = ({ zoneOption = [{ title: "", value: "" }] }) => {
    const dispatch = useStoreDispatch();
    const openConfirmDialog = useSelector(confirmDialogSelector);
    const basicDialogOpenFor = useSelector(basicDialogSelector);
    const { zones,
        setZones,
        referenceSinglePowerEnabled,
        setReferenceSinglePowerEnabled, submitOKHandler, handleClickOnStop, runOnDemandTaskStatus } = useRemScanData();

    const handleClose = () => {
        dispatch(setBasicDialogOpenFor(undefined));
    };

    const autocompleteSelectHandler = (event: any, newValue: any | null) => {
        setZones(newValue);
    };
    const checkboxHandler = (checked: boolean) => {
        setReferenceSinglePowerEnabled(checked ? "true" : "false");
    };

    const footer = (
        <>
            <Button variant="outline" onClick={handleClose}>
                Cancel
            </Button>
            <Button
                onClick={submitOKHandler}
                disabled={runOnDemandTaskStatus.isLoading}
            >
                {runOnDemandTaskStatus?.isLoading && (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                )}
                Yes, Start
            </Button>
        </>
    );

    return (
        <>
            {openConfirmDialog === DIALOGOPENFOR.REMSCANSTOP && (
                <ConfirmDialog actionName={`Yes, Stop`} handleClickAction={handleClickOnStop}>
                    Are you sure you want to stop LTE Rem Scan?
                </ConfirmDialog>
            )}
            {basicDialogOpenFor === DIALOGOPENFOR.REMSCANSTART && (
                <Sheet open={true} onClose={handleClose} title="LTE Rem Start" footer={footer} width="520px">
                    <div className="space-y-6 pt-2">
                        <div>
                            <AutocompleteSelect
                                name="zones"
                                value={zones}
                                multiple={true}
                                id={"zoneId"}
                                options={zoneOption}
                                label={"Select the Zones"}
                                margin={"normal"}
                                size={"medium"}
                                onChange={autocompleteSelectHandler}
                                autoFocus={true}
                            />
                            {zones.length <= 0 && (
                                <p className="text-sm text-muted-foreground mt-4">
                                    List of Zones- eg 0,1,2 - if not specified, REM is executed on Zone 0
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="referenceSinglePower"
                                checked={referenceSinglePowerEnabled === "true"}
                                onCheckedChange={(checked: boolean) => checkboxHandler(!!checked)}
                            />
                            <Label htmlFor="referenceSinglePower" className="cursor-pointer">
                                Reference Single Power Enabled
                            </Label>
                        </div>
                    </div>
                </Sheet>
            )}
        </>
    );
};

export default RemScanModal;